// frontend/src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/apiClient'; // For fetching fresh user data

// Create the context
const AuthContext = createContext(null);

// Create a provider component
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token')); // Load token initially
    const [loading, setLoading] = useState(true); // To handle initial auth check
    const navigate = useNavigate();

    // Memoized function to fetch current user details
    const fetchCurrentUser = useCallback(async (currentToken) => {
        if (!currentToken) {
            setUser(null); // Clear user if no token
            setLoading(false);
            return;
        }
        try {
            // The Authorization header should ideally be set by an Axios interceptor
            // based on the token from localStorage or state.
            // If not, ensure it's correctly applied for this request.
            const config = { headers: { Authorization: `Bearer ${currentToken}` } };
            const { data } = await apiClient.get('/api/auth/me', config);
            localStorage.setItem('user', JSON.stringify(data));
            setUser(data);

            // ✅ Check if user needs to accept TOS
            if (data.requiresTosAcceptance) {
                console.log('User requires TOS acceptance, redirecting to /tos');
                navigate('/tos', {
                    state: {
                        email: data.email,
                        existingUser: true
                    },
                    replace: true
                });
            }
        } catch (e) {
            console.error("Failed to fetch current user, clearing session:", e.response?.data?.msg || e.message);
            // Clear stored token and user if fetching fails (e.g., token expired)
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            setToken(null); // Clear token in state
            setUser(null);  // Clear user in state
            // Optionally navigate to login if the token is invalid and causes an error
            // if (e.response?.status === 401 || e.response?.status === 403) {
            //     navigate('/login');
            // }
        } finally {
            setLoading(false); // Done loading
        }
    }, [navigate, setUser, setLoading, setToken]); // Dependencies: navigate for potential redirection, and state setters.
                                     // apiClient is a module import, typically stable.

    // Effect to run on initial load to check for existing token and fetch user
    useEffect(() => {
        const storedToken = localStorage.getItem('token');
        if (storedToken) {
            // It's good practice to set the token state if it's found in localStorage,
            // though fetchCurrentUser will also implicitly use it if your apiClient is set up
            // to grab it from localStorage or if it's passed directly.
            setToken(storedToken);
            fetchCurrentUser(storedToken); // Fetch user details if token exists
        } else {
            setLoading(false); // No token, not loading, no user to fetch
        }
    }, [fetchCurrentUser]); // Dependency: fetchCurrentUser (which is memoized)

    // Login function to update state and storage
    const login = (userData, receivedToken) => {


        localStorage.setItem('token', receivedToken);
        localStorage.setItem('user', JSON.stringify(userData));
        setToken(receivedToken);
        setUser(userData);

        // ✅ Check if user needs to accept TOS after login
        if (userData.requiresTosAcceptance) {
            navigate('/tos', {
                state: {
                    email: userData.email,
                    existingUser: true
                },
                replace: true
            });
        } else {
            
            navigate('/dashboard');
        }
    };

    // Logout function
    const logout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setToken(null);
        setUser(null);
        navigate('/login'); // Navigate to login on logout
    };

    // Memoized function to refresh user data in the context
    // This is the function where the error likely originated or was related to.
    const refreshAuthContextUser = useCallback(async () => {
        const currentToken = localStorage.getItem('token');
        if (currentToken) {
            setLoading(true); // Indicate loading while refreshing
            await fetchCurrentUser(currentToken);
            // setLoading(false) is handled within fetchCurrentUser's finally block
        } else {
            // If no token, ensure user is cleared and not loading
            setUser(null);
            setLoading(false);
            console.log("refreshAuthContextUser: No token found, user cleared.");
        }
    }, [fetchCurrentUser, setLoading, setUser]); // Dependencies: fetchCurrentUser (memoized) and setLoading/setUser (stable state setters)


    // Value provided to consuming components
    const value = {
        isAuthenticated: !!token && !!user, // True if token and user object exist
        user,
        token,
        login,
        logout,
        isLoading: loading, // Expose loading state for initial auth check & refresh
        refreshAuthContextUser // Expose the refresh function
    };

    // Don't render children until initial loading is complete
    // to prevent flicker or premature redirects
    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

// Custom hook to easily use the Auth Context
export const useAuth = () => {
    return useContext(AuthContext);
};
